// src/pages/CapacityLimits.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Gauge, Plus, Trash2, X, Search, History } from 'lucide-react';
import { capacityLimitsService, CapacityLimit, LimitScope, ResolveResult } from 'api/services/capacityLimits.service';
import { useAuth } from 'hooks/useAuth';
import { useFeatureGrants } from 'hooks/useFeatureGrants';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import {
  isPlatformTier, mayDeleteLimit, PLATFORM_CARRIERS_COPY, holdsTelephonyGrant, CAPACITY_GRANT_COPY,
} from './telephonyAccessRules';
import { reasonError } from './capacityEnforcementRules';
import CapacityEnforcementPanel from './capacity/CapacityEnforcementPanel';
import CapacityHistoryModal from './capacity/CapacityHistoryModal';

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
const SCOPES: LimitScope[] = ['NUMBER', 'PROVIDER', 'ORG', 'CAMPAIGN'];
const MANUAL_SCOPES: LimitScope[] = ['NUMBER', 'CAMPAIGN']; // PROVIDER/ORG are DERIVED (managed on Providers)

const scopeLabel: Record<LimitScope, string> = {
  NUMBER: 'Number (DID)', PROVIDER: 'Provider (trunk)', ORG: 'Organization', CAMPAIGN: 'Campaign'
};
const scopeBadge = (s: LimitScope) => {
  const cls = s === 'NUMBER' ? 'bg-sky-100 text-sky-800' : s === 'PROVIDER' ? 'bg-purple-100 text-purple-800'
    : s === 'ORG' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{s}</span>;
};

const CapacityLimits: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canManage = ['master_admin', 'super_admin', 'service_admin', 'platform_admin', 'provider_owner', 'provider_admin'].includes(role || '');
  // A non-master is sent only their own tree's rows (their businesses, numbers, campaigns,
  // own trunks); platform trunk ceilings and other tenants' rows never reach them.
  const isMaster = isPlatformTier(role);
  // Writing limits is off for a provider until Green Giraffe grants CAPACITY_MANAGE (14 Oct);
  // without it the page stays a read-only view of their own rows.
  const grants = useFeatureGrants();
  const canManageLimits = canManage && holdsTelephonyGrant(role, grants, 'CAPACITY_MANAGE');

  const [filter, setFilter] = useState<LimitScope | ''>('');
  const [rows, setRows] = useState<CapacityLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await capacityLimitsService.list(filter || undefined);
    if (res.success) setRows(res.data || []);
    else setError(res.error || 'Failed to load');
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // add/edit modal (MANUAL tiers only)
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<CapacityLimit>({ scope: 'NUMBER', scope_value: '', cps: null, max_channels: null, enabled: true });
  const [formErr, setFormErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setForm({ scope: 'NUMBER', scope_value: '', cps: null, max_channels: null, enabled: true, reason: '' }); setFormErr(null); setModal(true); };
  const [history, setHistory] = useState<CapacityLimit | null>(null);
  const save = async () => {
    setFormErr(null);
    if (!form.scope_value.trim()) { setFormErr('Scope value is required'); return; }
    if (form.cps == null && form.max_channels == null) { setFormErr('Set CPS and/or max channels'); return; }
    const rErr = reasonError(form.reason);
    if (rErr) { setFormErr(rErr); return; }
    setSaving(true);
    const res = await capacityLimitsService.upsert({ ...form, scope_value: form.scope_value.trim(), reason: form.reason?.trim() });
    setSaving(false);
    if (res.success) { setModal(false); load(); } else setFormErr(res.error || 'Save failed');
  };
  const remove = async (l: CapacityLimit) => {
    if (!l.id) return;
    const reason = window.prompt(`Delete ${l.scope} limit for ${l.scope_value}?\n\nWhy? (required, kept in the capacity history)`);
    if (reason === null) return;
    const rErr = reasonError(reason);
    if (rErr) { alert(rErr); return; }
    const res = await capacityLimitsService.remove(l.id, reason.trim());
    if (res.success) load(); else alert(res.error || 'Delete failed');
  };

  // resolve preview
  const [rq, setRq] = useState({ number: '', trunk: '', businessId: '', campaignId: '' });
  const [rr, setRr] = useState<ResolveResult | null>(null);
  const [rErr, setRErr] = useState<string | null>(null);
  const runResolve = async () => {
    setRErr(null); setRr(null);
    const res = await capacityLimitsService.resolve({
      number: rq.number || undefined, trunk: rq.trunk || undefined,
      businessId: rq.businessId || undefined, campaignId: rq.campaignId || undefined
    });
    if (res.success) setRr(res.data || null); else setRErr(res.error || 'Resolve failed');
  };

  if (loading && rows.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Gauge className="w-6 h-6 text-blue-600" /> Capacity Limits</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          CPS + concurrent-channel caps across every tier. cpass is the source of truth; limits propagate to the runtime for local enforcement.
        </p>
        {!isMaster && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Showing your organization's limits only. Derived limits come from your trunk and routing setup and are read-only. {PLATFORM_CARRIERS_COPY}
          </p>
        )}
        {canManage && !canManageLimits && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{CAPACITY_GRANT_COPY}</p>
        )}
      </div>

      {isMaster && <CapacityEnforcementPanel onChanged={load} />}

      {/* resolve preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resolve — which limits gate a call &amp; the effective cap</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <input value={rq.number} onChange={e => setRq({ ...rq, number: e.target.value })} placeholder="number (DID)" className={`${INPUT} text-sm`} />
          <input value={rq.trunk} onChange={e => setRq({ ...rq, trunk: e.target.value })} placeholder={isMaster ? 'trunk (sip_endpoint)' : 'trunk (your own trunk id)'} className={`${INPUT} text-sm`} />
          <input value={rq.businessId} onChange={e => setRq({ ...rq, businessId: e.target.value })} placeholder="businessId" className={`${INPUT} text-sm`} />
          <input value={rq.campaignId} onChange={e => setRq({ ...rq, campaignId: e.target.value })} placeholder="campaignId" className={`${INPUT} text-sm`} />
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button onClick={runResolve} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 inline-flex items-center gap-2"><Search className="w-4 h-4" /> Resolve</button>
          {rr && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {rr.applied.map(a => <span key={a.id} className="inline-flex items-center gap-1">{scopeBadge(a.scope)}<span className="text-gray-500">{a.cps ?? '∞'}/{a.max_channels ?? '∞'}</span></span>)}
              <span className="font-semibold text-gray-900 dark:text-white">→ effective {rr.effectiveCps ?? '∞'} CPS / {rr.effectiveChannels ?? '∞'} ch</span>
            </div>
          )}
          {rErr && <span className="text-sm text-red-600">{rErr}</span>}
        </div>
      </div>

      {/* controls */}
      <div className="flex items-center justify-between gap-3">
        <select value={filter} onChange={e => setFilter((e.target.value || '') as LimitScope | '')} className={`${INPUT} md:w-60`}>
          <option value="">All tiers</option>
          {SCOPES.map(s => <option key={s} value={s}>{scopeLabel[s]}</option>)}
        </select>
        {canManageLimits && (
          <button onClick={openCreate} className="tl-btn tl-btn-primary"><Plus className="w-5 h-5" /> Add Limit</button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>{['Tier', 'Scope value', 'CPS', 'Channels', 'Source', 'Status', 'Actions'].map(h => (
                <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">{isMaster ? 'No limits configured.' : `No limits are set for your organization. ${PLATFORM_CARRIERS_COPY}`}</td></tr>}
              {rows.map(l => (
                <tr key={l.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">{scopeBadge(l.scope)}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-white">{l.scope_value}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{l.cps ?? '∞'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{l.max_channels ?? '∞'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${l.source === 'DERIVED' ? 'text-gray-400 italic' : 'text-gray-600 dark:text-gray-400'}`}>{l.source || 'MANUAL'}</span>
                  </td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${l.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{l.enabled ? 'Enabled' : 'Disabled'}</span></td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <button onClick={() => setHistory(l)} className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="History"><History className="w-4 h-4" /></button>
                    {canManageLimits && mayDeleteLimit(l, isMaster)
                      ? <button onClick={() => remove(l)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      : <span className="text-xs text-gray-400 italic" title={isMaster ? 'Managed from the Providers screen' : 'Read-only: managed by Green Giraffe'}>{l.source === 'DERIVED' ? 'derived' : '—'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {history && history.scope_value && (
        <CapacityHistoryModal scope={history.scope} value={history.scope_value} title={`${history.scope} ${history.scope_value}`}
          onClose={() => setHistory(null)} />
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Capacity Limit</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {formErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{formErr}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tier</label>
                  <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value as LimitScope })} className={INPUT}>
                    {MANUAL_SCOPES.map(s => <option key={s} value={s}>{scopeLabel[s]}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Provider/Org limits are set on the Providers screen.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {form.scope === 'NUMBER' ? 'Number (E.164)' : 'Campaign ID'}
                  </label>
                  <input value={form.scope_value} onChange={e => setForm({ ...form, scope_value: e.target.value })} className={`${INPUT} font-mono`} placeholder={form.scope === 'NUMBER' ? '+912265197300' : 'camp-diwali'} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPS</label>
                  <input type="number" min={0} value={form.cps ?? ''} onChange={e => setForm({ ...form, cps: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="unset" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max channels</label>
                  <input type="number" min={0} value={form.max_channels ?? ''} onChange={e => setForm({ ...form, max_channels: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="unset" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-blue-600" /> Enabled
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (required)</label>
                <textarea value={form.reason || ''} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2} className={INPUT}
                  placeholder="Why this limit? Kept in the capacity history with your name." />
                <p className="text-xs text-gray-400 mt-1">Recorded in cpass; not yet enforced on the switch.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">
                {saving && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapacityLimits;
